import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {ChangePatronEmailValue} from '@store/patron/patron.types';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {buildFormGroup, getExtendedControlFrom} from '@aviary/forms/form.helpers';
import {InputFormFieldComponent} from '@aviary/forms/fields/input-form-field/input-form-field.component';
import {PersistentFormDialogComponent} from '@aviary/dialogs/persistent-form-dialog/persistent-form-dialog.component';
import {PersistentFormDialog, PersistentFormDialogDefinition} from '@aviary/dialogs/persistent-form-dialog/persistent-form-dialog.types';
import {RegistrationValidators} from '@store/registration/registration.validators';
import {isDuplicateEmailFailure} from '@store/patron/patron.helpers';

export interface ChangeEmailDialogData {
  email: string;
}

@Component({
  selector: 'px-change-email',
  template: `
    <mx-persistent-form-dialog [definition]="dialogDefinition">
      <div class="flex flex-col">
        <p class="text-title-large">Current Email</p>
        <p class="text-body-large break-all">{{ dialogData.email }}</p>
      </div>

      <mx-input-form-field fieldName="email" overrideLabel="New Email" placeholder="example@example.com" [formGroup]="formGroup"/>
    </mx-persistent-form-dialog>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PersistentFormDialogComponent, InputFormFieldComponent]
})
export class ChangeEmailDialog implements PersistentFormDialog<ChangeEmailDialogData, ChangePatronEmailValue> {

  public readonly dialogData: ChangeEmailDialogData = inject(MAT_DIALOG_DATA);
  public readonly formGroup: ExtendedFormGroup = buildFormGroup<ChangePatronEmailValue>(
    {email: ''},
    inject(RegistrationValidators).changeEmailFormValidator
  );

  public readonly dialogDefinition: PersistentFormDialogDefinition<ChangePatronEmailValue> = {
    title: 'Change Email',
    prompt: 'Once submitted, you will receive an email to verify your email address. You’ll need to click the link in your email to complete the change.',
    confirmButtonLabel: 'Send Verification Email',
    formGroup: this.formGroup
  };

  private readonly emailControl: ExtendedFormControl = getExtendedControlFrom(this.formGroup, 'email');

  // Covers the email being taken between the async availability check and submission.
  public handleSubmissionFailure(error: unknown): void {
    if (!isDuplicateEmailFailure(error)) return;

    this.emailControl.setErrors({...this.emailControl.errors, patronEmailUnavailable: true});
    this.emailControl.markAsTouched();
  }
}
